import { cn } from '@lib/cn';
import { Badge } from '@shared/ui';

import type { RecordTable as RecordTableData, ValueCell } from '../model/module-report-types';

/**
 * A record as its module describes it: a Salesforce → Veevart 2.0 mapping
 * (Attribute | Salesforce | → | Veevart 2.0 | Status), or, for a module
 * without one, its columns as label → value.
 */

const HEAD =
    'pb-2 pr-2.5 text-left text-[11.5px] font-semibold uppercase tracking-wider text-content-muted';
const CELL = 'border-b border-line-subtle py-3 pr-2.5 align-top group-last:border-0';

function Value({ cell }: { cell: ValueCell }) {
    return (
        <>
            {cell.text === null ? (
                <span className="italic text-content-muted">{cell.placeholder ?? '—'}</span>
            ) : (
                <span className="inline-flex flex-wrap items-center gap-2">
                    {cell.swatch && (
                        <span
                            aria-hidden
                            className="size-[18px] flex-none rounded border border-line"
                            // The record's own colour: validated `#RRGGBB` (swatchHex).
                            style={{ backgroundColor: cell.swatch }}
                        />
                    )}
                    <span
                        title={cell.full ?? undefined}
                        className={cn(
                            'min-w-0 [overflow-wrap:anywhere]',
                            cell.mono && 'font-mono text-[12px]',
                        )}
                    >
                        {cell.text}
                    </span>
                </span>
            )}
            {cell.note && (
                <span className="mt-0.5 block text-[12.5px] text-content-muted">{cell.note}</span>
            )}
        </>
    );
}

export function RecordTable({ table }: { table: RecordTableData }) {
    if (table.kind === 'values') {
        return (
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-[13.5px]">
                {table.rows.map((row) => (
                    <div key={row.label} className="contents">
                        <dt className="text-content-muted">{row.label}</dt>
                        <dd className="min-w-0 [overflow-wrap:anywhere]">{row.value}</dd>
                    </div>
                ))}
            </dl>
        );
    }
    return (
        <>
            {/* A phone: each attribute stacked, nothing hidden behind a scroll. */}
            <dl className="grid gap-3 sm:hidden print:hidden">
                {table.rows.map((row) => (
                    <div
                        key={row.attribute}
                        className="grid gap-1.5 border-b border-line-subtle pb-3 text-[13.5px] last:border-0"
                    >
                        <dt className="flex items-center justify-between gap-3 font-medium">
                            {row.attribute}
                            <Badge tone={row.status.tone}>{row.status.label}</Badge>
                        </dt>
                        <dd className="flex flex-wrap items-start gap-x-2 gap-y-1">
                            <span className="min-w-0">
                                <Value cell={row.salesforce} />
                            </span>
                            <span aria-label="maps to" className="text-content-muted">
                                →
                            </span>
                            <span className="min-w-0">
                                <Value cell={row.veevart} />
                            </span>
                        </dd>
                    </div>
                ))}
            </dl>
            <table className="hidden w-full table-fixed border-collapse text-[13.5px] sm:table print:table print:text-[12px]">
                <colgroup>
                    <col className="w-[18%]" />
                    <col className="w-[28%]" />
                    <col className="w-[4%]" />
                    <col className="w-[30%]" />
                    <col className="w-[20%]" />
                </colgroup>
                <thead>
                    <tr className="border-b border-line">
                        <th className={HEAD}>Attribute</th>
                        <th className={HEAD}>Salesforce</th>
                        <th className={HEAD}>
                            <span className="sr-only">maps to</span>
                        </th>
                        <th className={HEAD}>Veevart 2.0</th>
                        <th className={HEAD}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {table.rows.map((row) => (
                        <tr key={row.attribute} className="group">
                            <td className={cn(CELL, 'font-medium')}>{row.attribute}</td>
                            <td className={CELL}>
                                <Value cell={row.salesforce} />
                            </td>
                            <td aria-hidden className={cn(CELL, 'px-1 text-content-muted')}>
                                →
                            </td>
                            <td className={CELL}>
                                <Value cell={row.veevart} />
                            </td>
                            <td className={CELL}>
                                {/* A narrow column: a long verdict wraps rather than overflows. */}
                                <Badge tone={row.status.tone} className="whitespace-normal">
                                    {row.status.label}
                                </Badge>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}
